import React from "react";
import LegalLayout, { H2, P, UL, LI, CALLOUT } from "../components/LegalLayout";
import { LockIllustration } from "../components/legal/LegalIllustrations";

const SECTIONS = [
  { id: "controller", labelFr: "Responsable du traitement", labelEn: "Data controller" },
  { id: "data", labelFr: "Données collectées", labelEn: "Data we collect" },
  { id: "purposes", labelFr: "Finalités", labelEn: "Purposes" },
  { id: "sharing", labelFr: "Partage des données", labelEn: "Data sharing" },
  { id: "retention", labelFr: "Conservation", labelEn: "Retention" },
  { id: "rights", labelFr: "Tes droits", labelEn: "Your rights" },
  { id: "security", labelFr: "Sécurité", labelEn: "Security" },
  { id: "minors", labelFr: "Mineurs", labelEn: "Minors" },
  { id: "cookies", labelFr: "Cookies", labelEn: "Cookies" },
];

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      titleFr="Politique de confidentialité" titleEn="Privacy Policy"
      taglineFr="Les informations que nous recevons, et l'usage qu'on en fait."
      taglineEn="The information we receive, and how we use it."
      updatedAt="11 septembre 2026"
      illustration={<LockIllustration />}
      sections={SECTIONS}
      tldrFr={[
        "Nous ne vendons jamais tes données, à personne.",
        "Le contenu de tes messages est chiffré au repos, tes appels audio/vidéo ne passent jamais par nos serveurs.",
        "Tes données sont supprimées ou anonymisées 12 mois après la suppression de ton compte.",
        "Tu peux accéder, corriger ou effacer tes données depuis les réglages, à tout moment.",
      ]}
      tldrEn={[
        "We never sell your data, to anyone.",
        "Your message content is encrypted at rest, your audio/video calls never pass through our servers.",
        "Your data is deleted or anonymized 12 months after your account is deleted.",
        "You can access, correct or erase your data from settings, at any time.",
      ]}
    >
      {(lang) => lang === "fr" ? (
        <>
          <P>
            La présente politique de confidentialité décrit comment SAS LNV
            {" "}(ci-après « OLTODO », « nous »), éditeur de l'application et du site OLTODO, collecte, utilise et
            protège les données personnelles des utilisateurs (« toi ») conformément au Règlement Général sur la
            Protection des Données (RGPD) et à la loi Informatique et Libertés.
          </P>

          <H2 id="controller">1. Responsable du traitement</H2>
          <P>
            SAS LNV, Société par Actions Simplifiée au capital de 100 000 €,
            immatriculée au RCS de Paris sous le numéro 109 515 288 (SIRET 109 515 288 00016),
            dont le siège social est situé 50 avenue des Champs-Élysées, 75008 Paris, France.
            Contact, contact@oltodo.com.
          </P>

          <H2 id="data">2. Données que nous collectons</H2>
          <P>Selon ton usage de l'application, nous collectons :</P>
          <UL>
            <LI><strong>Données de compte</strong>, nom, email, téléphone, mot de passe chiffré, photo de profil, biographie.</LI>
            <LI><strong>Photos de vérification</strong>, selfie (obligatoire) et pièce justificative (facultative), transmises uniquement à l'équipe de modération et supprimées définitivement dès la décision.</LI>
            <LI><strong>Données de localisation</strong>, ville et coordonnées géographiques, pour te proposer des activités et des personnes à proximité.</LI>
            <LI><strong>Contenus publiés</strong>, publications, photos, vidéos, commentaires, messages, avis.</LI>
            <LI><strong>Données d'utilisation</strong>, interactions avec l'application, historique de participation, évaluations reçues et données.</LI>
            <LI><strong>Données de paiement</strong>, lors d'un paiement, les informations de carte bancaire sont traitées directement par Stripe et ne sont jamais stockées sur nos serveurs.</LI>
            <LI><strong>Données techniques</strong>, type d'appareil, système d'exploitation, adresse IP, identifiants de notification push.</LI>
          </UL>

          <H2 id="purposes">3. Finalités et bases légales</H2>
          <UL>
            <LI>Fourniture du service (création de compte, mise en relation, réservations), exécution du contrat.</LI>
            <LI>Vérification d'identité, sécurité, prévention de la fraude et modération, intérêt légitime.</LI>
            <LI>Communications relatives au service (notifications, emails transactionnels), exécution du contrat.</LI>
            <LI>Amélioration du service et statistiques d'usage, intérêt légitime.</LI>
            <LI>Communications marketing, si tu y as consenti, consentement.</LI>
          </UL>

          <H2 id="sharing">4. Partage des données</H2>
          <P>Nous ne vendons jamais tes données. Elles peuvent être partagées avec :</P>
          <UL>
            <LI>D'autres membres, dans la limite de ce que tu rends visible sur ton profil et tes publications.</LI>
            <LI>Nos sous-traitants techniques, hébergement (Groupe LWS SARL, 2 rue Jules Ferry, 88190 Golbey, France), stockage des médias (Cloudinary), paiement (Stripe), envoi d'emails (Resend), chacun agissant sous nos instructions et engagé contractuellement à protéger tes données.</LI>
            <LI>Les autorités compétentes, si la loi nous y oblige.</LI>
          </UL>

          <H2 id="retention">5. Durée de conservation</H2>
          <P>
            Tes données sont conservées pendant la durée de ton compte, puis supprimées ou anonymisées dans un
            délai de 12 mois après sa suppression, sauf obligation légale de conservation plus longue. Les
            factures et reçus (qui contiennent ton nom et, le cas échéant, ton adresse) sont conservés
            10&nbsp;ans au titre de l'obligation comptable et fiscale, base légale «&nbsp;obligation légale&nbsp;».
          </P>

          <H2 id="rights">6. Tes droits</H2>
          <P>Conformément au RGPD, tu disposes d'un droit d'accès, de rectification, d'effacement, de limitation,
          de portabilité et d'opposition sur tes données. Tu peux exercer ces droits directement depuis les
          réglages de l'application (préférences de notification, comptes masqués/bloqués, suppression de
          compte) ou en écrivant à contact@oltodo.com. Tu peux également introduire une réclamation auprès de la
          CNIL (www.cnil.fr).</P>

          <H2 id="security">7. Sécurité</H2>
          <P>Nous mettons en œuvre des mesures techniques et organisationnelles raisonnables (chiffrement des mots
          de passe, connexions HTTPS, contrôle d'accès) pour protéger tes données contre l'accès non autorisé, la
          perte ou l'altération.</P>
          <CALLOUT>
            Le contenu de tes messages est chiffré au repos dans notre base de données, avec une clé détenue
            exclusivement par nos serveurs, il n'apparaît jamais en clair dans une sauvegarde ou une fuite de
            données. Tes appels audio et vidéo transitent directement entre les appareils des participants et ne
            passent jamais par nos serveurs.
          </CALLOUT>
          <P>Un nombre restreint de responsables autorisés d'OLTODO peuvent accéder au contenu d'une conversation,
          à ta position en direct si tu l'as activée, ou se connecter temporairement à ton compte pour reproduire
          un problème, dans le cadre d'une modération, d'un support technique, d'une enquête de sécurité ou d'une
          obligation légale, jamais à ton mot de passe, qui reste toujours haché et inconnu de nous.</P>

          <H2 id="minors">8. Mineurs</H2>
          <P>OLTODO n'est pas destiné aux personnes de moins de 18 ans. Nous ne collectons pas sciemment de
          données concernant des mineurs de cet âge.</P>

          <H2 id="cookies">9. Cookies</H2>
          <P>Le site utilise du stockage strictement nécessaire à son fonctionnement (session, préférences).
          Aucun cookie publicitaire tiers n'est utilisé à ce jour, voir la page Cookies pour le détail.</P>

          <H2>10. Modifications</H2>
          <P>Cette politique peut être mise à jour, toute modification substantielle te sera notifiée dans
          l'application.</P>

          <H2>11. Contact</H2>
          <P>Pour toute question relative à tes données personnelles, contact@oltodo.com.</P>
        </>
      ) : (
        <>
          <P>
            This Privacy Policy describes how SAS LNV (“OLTODO”, “we”), publisher of the
            OLTODO application and website, collects, uses and protects personal data of users (“you”) in
            accordance with the General Data Protection Regulation (GDPR).
          </P>

          <H2 id="controller">1. Data Controller</H2>
          <P>
            SAS LNV, a French simplified joint-stock company (SAS) with share capital of
            €100,000, registered under company number (RCS Paris) 109 515 288 (SIRET 109 515 288 00016), with
            registered office at 50 avenue des Champs-Élysées, 75008 Paris, France. Contact, contact@oltodo.com.
          </P>

          <H2 id="data">2. Data We Collect</H2>
          <UL>
            <LI><strong>Account data</strong>, name, email, phone number, encrypted password, profile photo, bio.</LI>
            <LI><strong>Verification photos</strong>, a selfie (required) and a supporting document (optional), sent only to the moderation team and permanently deleted once a decision is made.</LI>
            <LI><strong>Location data</strong>, city and geographic coordinates, to suggest nearby activities and people.</LI>
            <LI><strong>Content you post</strong>, publications, photos, videos, comments, messages, reviews.</LI>
            <LI><strong>Usage data</strong>, interactions with the app, activity participation history, ratings received and given.</LI>
            <LI><strong>Payment data</strong>, card details are processed directly by Stripe and never stored on our servers.</LI>
            <LI><strong>Technical data</strong>, device type, operating system, IP address, push notification tokens.</LI>
          </UL>

          <H2 id="purposes">3. Purposes and Legal Bases</H2>
          <UL>
            <LI>Providing the service (account creation, matching, bookings), contract performance.</LI>
            <LI>Identity verification, security, fraud prevention and moderation, legitimate interest.</LI>
            <LI>Service-related communications (notifications, transactional emails), contract performance.</LI>
            <LI>Service improvement and usage statistics, legitimate interest.</LI>
            <LI>Marketing communications, where you have consented, consent.</LI>
          </UL>

          <H2 id="sharing">4. Data Sharing</H2>
          <P>We never sell your data. It may be shared with:</P>
          <UL>
            <LI>Other members, limited to what you make visible on your profile and posts.</LI>
            <LI>Our technical processors, hosting (Groupe LWS SARL, 2 rue Jules Ferry, 88190 Golbey, France), media storage (Cloudinary), payments (Stripe), transactional email (Resend), each acting under our instructions and contractually bound to protect your data.</LI>
            <LI>Competent authorities, where required by law.</LI>
          </UL>

          <H2 id="retention">5. Data Retention</H2>
          <P>
            Your data is retained for the duration of your account, then deleted or anonymized within 12 months
            after account deletion, unless a longer legal retention period applies. Invoices and receipts
            (which contain your name and, where applicable, your address) are kept for 10 years under
            accounting and tax obligations, legal basis "legal obligation".
          </P>

          <H2 id="rights">6. Your Rights</H2>
          <P>Under the GDPR, you have the right to access, rectify, erase, restrict, port and object to the
          processing of your data. You can exercise these rights directly from the app settings (notification
          preferences, hidden/blocked accounts, account deletion) or by writing to contact@oltodo.com. You may
          also lodge a complaint with your local data protection authority.</P>

          <H2 id="security">7. Security</H2>
          <P>We implement reasonable technical and organizational measures (password encryption, HTTPS
          connections, access control) to protect your data against unauthorized access, loss or alteration.</P>
          <CALLOUT>
            Your message content is encrypted at rest in our database, with a key held exclusively by our
            servers, it never appears in plain text in a backup or a data breach. Your audio and video calls
            travel directly between participants' devices and never pass through our servers.
          </CALLOUT>
          <P>A limited number of authorized OLTODO staff may access the content of a conversation, your live
          location if you have enabled it, or temporarily sign in to your account to reproduce an issue, for
          moderation, technical support, security investigation, or legal compliance purposes, never your
          password, which remains hashed and unknown to us at all times.</P>

          <H2 id="minors">8. Minors</H2>
          <P>OLTODO is not intended for individuals under 18. We do not knowingly collect data from minors of
          this age.</P>

          <H2 id="cookies">9. Cookies</H2>
          <P>The website uses storage strictly necessary for its operation (session, preferences). No
          third-party advertising cookies are used at this time, see the Cookies page for detail.</P>

          <H2>10. Changes</H2>
          <P>This policy may be updated, any material change will be notified to you within the app.</P>

          <H2>11. Contact</H2>
          <P>For any question about your personal data, contact@oltodo.com.</P>
        </>
      )}
    </LegalLayout>
  );
}
